/**
 * Scoring utilities for FHIR-native assessment scoring.
 *
 * Handles reading/writing itemWeight extensions, computing scores from
 * QuestionnaireResponse answers, and managing ScoringConfiguration
 * serialization on Questionnaire extensions.
 */
import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import {
  ITEM_WEIGHT_URL,
  SCORING_CONFIG_URL,
  type ScoringConfiguration,
  type InterpretationBand,
} from '../scoringTypes';
import { emptyScoringConfig } from '../data/mockScoringData';

// ---------------------------------------------------------------------------
// itemWeight read/write on answer options
// ---------------------------------------------------------------------------

type WeightExt = { url: string; valueDecimal?: number };

/** Read itemWeight from valueCoding.extension (canonical); fall back to legacy answerOption.extension. */
export function getItemWeight(option: {
  extension?: WeightExt[];
  valueCoding?: { extension?: WeightExt[] };
}): number | undefined {
  const onCoding = option.valueCoding?.extension?.find((e) => e.url === ITEM_WEIGHT_URL);
  if (onCoding?.valueDecimal != null) return onCoding.valueDecimal;
  const legacy = option.extension?.find((e) => e.url === ITEM_WEIGHT_URL);
  return legacy?.valueDecimal;
}

/** Set itemWeight on valueCoding.extension per FHIR context for itemWeight (Coding). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setItemWeight(option: any, weight: number): any {
  const vc = { ...(option.valueCoding ?? {}) };
  const onCoding = ((vc.extension ?? []) as WeightExt[]).filter((e) => e.url !== ITEM_WEIGHT_URL);
  const topExt = ((option.extension ?? []) as WeightExt[]).filter((e) => e.url !== ITEM_WEIGHT_URL);
  const result: Record<string, unknown> = {
    ...option,
    valueCoding: {
      ...vc,
      extension: [...onCoding, { url: ITEM_WEIGHT_URL, valueDecimal: weight }],
    },
  };
  if (topExt.length > 0) result.extension = topExt;
  else delete result.extension;
  return result;
}

/** Get all scored items (choice/open-choice with answerOption) from a flat item list. */
export function getScoredItems(items: QuestionnaireItem[]): QuestionnaireItem[] {
  return items.filter(
    (item) => (item.type === 'choice' || item.type === 'open-choice') && item.answerOption?.length,
  );
}

/** Compute max possible weight for a single item (max of its answer option weights). */
export function getItemMaxWeight(item: QuestionnaireItem): number {
  if (!item.answerOption?.length) return 0;
  return Math.max(
    ...item.answerOption.map((opt) => getItemWeight(opt) ?? 0),
  );
}

/** Compute total max score across scored items (or a subset by linkId).
 *  Accepts pre-filtered scored items to avoid redundant getScoredItems calls. */
export function computeMaxScore(
  items: QuestionnaireItem[],
  includedLinkIds?: string[],
): number {
  const filtered = includedLinkIds?.length
    ? items.filter((item) => includedLinkIds.includes(item.linkId))
    : items;
  return filtered.reduce((sum, item) => sum + getItemMaxWeight(item), 0);
}

// ---------------------------------------------------------------------------
// ScoringConfiguration serialization (stored as extension on Questionnaire)
// ---------------------------------------------------------------------------

/** Read the ScoringConfiguration from a Questionnaire's extensions. */
export function getScoringConfig(questionnaire: Questionnaire): ScoringConfiguration {
  const ext = questionnaire.extension?.find((e) => e.url === SCORING_CONFIG_URL);
  if (!ext?.valueString) return emptyScoringConfig();
  try {
    return JSON.parse(ext.valueString) as ScoringConfiguration;
  } catch {
    return emptyScoringConfig();
  }
}

/** Write the ScoringConfiguration as an extension on a Questionnaire (returns a shallow copy). */
export function setScoringConfig(
  questionnaire: Questionnaire,
  config: ScoringConfiguration,
): Questionnaire {
  const existing = (questionnaire.extension ?? []).filter((e) => e.url !== SCORING_CONFIG_URL);
  const hasContent =
    config.outputs.length > 0 ||
    Object.keys(config.bands).length > 0 ||
    config.riskFlags.length > 0;

  if (!hasContent) {
    return { ...questionnaire, extension: existing.length > 0 ? existing : undefined };
  }

  return {
    ...questionnaire,
    extension: [
      ...existing,
      { url: SCORING_CONFIG_URL, valueString: JSON.stringify(config) },
    ],
  };
}

// ---------------------------------------------------------------------------
// Update item weights within the items array
// ---------------------------------------------------------------------------

/** Update the weight of a specific answer option on a specific item. Returns a new items array. */
export function updateItemOptionWeight(
  items: QuestionnaireItem[],
  itemLinkId: string,
  optionIndex: number,
  newWeight: number,
): QuestionnaireItem[] {
  return items.map((item) => {
    if (item.linkId !== itemLinkId) return item;
    const newOptions = (item.answerOption ?? []).map((opt, idx) => {
      if (idx !== optionIndex) return opt;
      return setItemWeight(opt, newWeight);
    });
    return { ...item, answerOption: newOptions };
  });
}

// ---------------------------------------------------------------------------
// Interpretation band validation
// ---------------------------------------------------------------------------

export interface BandValidationResult {
  errors: string[];
  warnings: string[];
}

/** Validate interpretation bands for overlaps and gaps. */
export function validateBands(bands: InterpretationBand[], maxScore?: number): BandValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (bands.length === 0) return { errors, warnings };

  const sorted = [...bands].sort((a, b) => a.low - b.low);

  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    if (current.low > current.high) {
      errors.push(`"${current.label}": low (${current.low}) exceeds high (${current.high})`);
    }
    if (current.low < 0) {
      errors.push(`"${current.label}": low value cannot be negative`);
    }
    if (!current.label.trim()) {
      errors.push(`Band ${i + 1}: label is required`);
    }
  }

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (curr.low <= prev.high) {
      errors.push(`"${prev.label}" (${prev.low}–${prev.high}) overlaps with "${curr.label}" (${curr.low}–${curr.high})`);
    } else if (curr.low > prev.high + 1) {
      warnings.push(`Gap between "${prev.label}" and "${curr.label}": scores ${prev.high + 1}–${curr.low - 1} are unclassified`);
    }
  }

  if (sorted.length > 0 && sorted[0].low > 0) {
    warnings.push(`Scores 0–${sorted[0].low - 1} are unclassified (first band starts at ${sorted[0].low})`);
  }

  if (maxScore !== undefined && sorted.length > 0) {
    const lastHigh = sorted[sorted.length - 1].high;
    if (lastHigh < maxScore) {
      warnings.push(`Scores ${lastHigh + 1}–${maxScore} are unclassified (last band ends at ${lastHigh})`);
    }
  }

  return { errors, warnings };
}

/** Generate a stable ID for new scoring entities. */
export function generateScoringId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
